import { Fragment } from 'react';
import {
  APP_ROLES,
  MAINTAINER_MCP_TOOLS,
  MCP_TOOLS,
  NEW_MEMBER_MCP_TOOLS,
  ROOT_MCP_TOOLS,
  mcpToolAllowed,
  type McpTool,
  type McpToolParam,
} from '@infras/shared-types';
import './McpToolReference.css';

/**
 * Live slot for docs/mcp.md (`<!-- docs:live component="McpToolReference" -->`).
 *
 * The MCP tool catalog by category, rendered from the SAME `MCP_TOOLS` array the MCP
 * server registers, so the reference cannot drift from the server. Every tag comes from
 * a shared set, never a hand list: `writes` from the catalog flag, the role gate from
 * ROOT_MCP_TOOLS / MAINTAINER_MCP_TOOLS, `new member` from NEW_MEMBER_MCP_TOOLS. A tool
 * added to a gate needs no docs edit to say so.
 *
 * Data only. The sentences that explain the tags and the tiers live in the markdown.
 * Open entries, not cards: a strong rule under each category, a hairline between tools.
 */
const CATEGORIES: { key: McpTool['category']; label: string; note?: string }[] = [
  { key: 'search', label: 'Search' },
  { key: 'repos', label: 'Repos' },
  { key: 'clusters', label: 'Clusters' },
  { key: 'databases', label: 'Databases' },
  { key: 'secrets', label: 'Secrets', note: 'names only, never values' },
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'access', label: 'Access requests' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'vpn', label: 'SecureLink' },
];

/** The role gate of a tool, or null when every role from engineer up may call it. */
function roleGate(name: string): string | null {
  if (ROOT_MCP_TOOLS.includes(name)) return 'root only';
  if (MAINTAINER_MCP_TOOLS.includes(name)) return 'root or maintainer';
  return null;
}

/** Catalog text marks identifiers with backticks; show those as code. */
function withCode(text: string) {
  return text.split(/`([^`]+)`/).map((part, i) => (i % 2 ? <code key={i}>{part}</code> : part));
}

const plural = (n: number) => `${n} tool${n === 1 ? '' : 's'}`;

function typeOf(p: McpToolParam): string {
  return p.type === 'array' && p.items ? `array of ${p.items.type}` : p.type;
}

export function McpToolReference() {
  const known = new Set<string>(CATEGORIES.map((c) => c.key));
  const groups = [
    ...CATEGORIES.map((c) => ({ ...c, id: c.key as string, tools: MCP_TOOLS.filter((t) => t.category === c.key) })),
    // A category added to the catalog before it gets a label here still shows up.
    {
      id: 'other',
      label: 'Other',
      note: undefined,
      tools: MCP_TOOLS.filter((t) => !known.has(t.category)),
    },
  ].filter((g) => g.tools.length > 0);

  if (groups.length === 0) {
    return <p className="doc-p doc-tools-empty">The server registers no tools.</p>;
  }

  return (
    <div className="doc-tools">
      <p className="doc-tools-summary">
        {plural(MCP_TOOLS.length)} in all. Listed for{' '}
        {APP_ROLES.map((role, i) => (
          <Fragment key={role}>
            {i > 0 && ' · '}
            {role.replace('_', ' ')}{' '}
            <strong>{MCP_TOOLS.filter((t) => mcpToolAllowed(role, t.name)).length}</strong>
          </Fragment>
        ))}
        .
      </p>
      <nav className="doc-tools-index" aria-label="Tool categories">
        {groups.map((g) => (
          <a key={g.id} href={`#${g.id}`}>
            {g.label}
            <span className="doc-tools-n">{g.tools.length}</span>
          </a>
        ))}
      </nav>

      {groups.map((g) => (
        <Fragment key={g.id}>
          <h3 className="doc-h3 doc-tools-cat" id={g.id}>
            {g.label}
            <span className="doc-tools-count">
              {plural(g.tools.length)}
              {g.note && ` · ${g.note}`}
            </span>
          </h3>
          {g.tools.map((t) => (
            <ToolEntry key={t.name} tool={t} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function ToolEntry({ tool }: { tool: McpTool }) {
  const params = Object.entries(tool.inputSchema.properties);
  const required = new Set(tool.inputSchema.required ?? []);
  const gate = roleGate(tool.name);
  return (
    <article className="doc-tool" id={`tool-${tool.name}`}>
      <header className="doc-tool-head">
        <h4 className="doc-tool-name">{tool.name}</h4>
        <span className="doc-tool-title">{tool.title}</span>
        <span className="doc-tool-tags">
          {tool.writes && <span className="doc-tool-tag doc-tool-tag--writes">writes</span>}
          {gate && <span className="doc-tool-tag doc-tool-tag--gate">{gate}</span>}
          {NEW_MEMBER_MCP_TOOLS.includes(tool.name) && <span className="doc-tool-tag">new member</span>}
        </span>
      </header>
      {tool.description.split(/\n\n+/).map((para, i) => (
        <p key={i} className="doc-p doc-tool-desc">
          {withCode(para)}
        </p>
      ))}
      <pre className="doc-code">
        <code>{tool.example.split('  ·  ').join('\n')}</code>
      </pre>
      {params.length === 0 ? (
        <p className="doc-tool-none">No parameters.</p>
      ) : (
        <div className="doc-table doc-tool-params">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {params.map(([name, p]) => (
                <tr key={name}>
                  <td>
                    <code>{name}</code>
                  </td>
                  <td>{typeOf(p)}</td>
                  <td>
                    {required.has(name) ? (
                      <span className="doc-yes">
                        <span className="doc-vh">required</span>
                      </span>
                    ) : (
                      <span className="doc-no">
                        <span className="doc-vh">optional</span>
                      </span>
                    )}
                  </td>
                  <td>
                    {withCode(p.description)}
                    {p.enum && (
                      <span className="doc-tool-enum">
                        One of{' '}
                        {p.enum.map((v, i) => (
                          <Fragment key={String(v)}>
                            {i > 0 && ', '}
                            <code>{String(v)}</code>
                          </Fragment>
                        ))}
                        .
                      </span>
                    )}
                    {p.maxLength !== undefined && (
                      <span className="doc-tool-enum">At most {p.maxLength} characters.</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}
